/**
 * DigitalCredentials - Digital ID Badge and Certificate Management
 * Features: View, Download, Share, Order Physical Copies
 */
import React, { useState } from 'react';
import { getToken as sharedGetToken } from '@/lib/authToken';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { 
  Plus, Award, Trash2, Loader2, Users, CheckCircle, Search, FileDown, 
  CreditCard, Share2, QrCode, Eye, ShoppingCart, Mail, Printer, Copy,
  BadgeCheck, User, Calendar, Building2, ExternalLink, Palette
} from 'lucide-react';
import api, { getErrorMessage } from '@/lib/api';
import CertificateDesigner from './CertificateDesigner';
import BadgeDesigner from './BadgeDesigner';

const CERT_TYPES = [
  { value: 'attendance', label: 'Attendance', color: 'bg-blue-500' },
  { value: 'completion', label: 'Completion', color: 'bg-green-500' },
  { value: 'speaker', label: 'Speaker', color: 'bg-purple-500' },
  { value: 'volunteer', label: 'Volunteer', color: 'bg-orange-500' },
  { value: 'achievement', label: 'Achievement', color: 'bg-amber-500' },
];

// Digital ID Badge Preview Component
const DigitalBadgePreview = ({ delegate, event, onDownload }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  
  return (
    <div className="perspective-1000">
      <div 
        className={`relative w-80 h-48 cursor-pointer transition-transform duration-500 transform-style-preserve-3d ${isFlipped ? 'rotate-y-180' : ''}`}
        onClick={() => setIsFlipped(!isFlipped)}
      >
        {/* Front of Badge */}
        <div className="absolute w-full h-full backface-hidden">
          <div className="w-full h-full bg-gradient-to-br from-indigo-600 to-purple-700 rounded-xl shadow-lg p-4 text-white">
            <div className="flex justify-between items-start mb-3">
              <div>
                <p className="text-xs opacity-75">{event?.name || 'Event'}</p>
                <h3 className="text-xl font-bold">{delegate?.name || 'Attendee'}</h3>
              </div>
              <div className="w-16 h-16 bg-white rounded-lg flex items-center justify-center">
                <QrCode className="h-12 w-12 text-indigo-600" />
              </div>
            </div>
            <div className="space-y-1">
              {delegate?.company && (
                <p className="text-sm flex items-center gap-1">
                  <Building2 className="h-3 w-3" /> {delegate.company}
                </p>
              )}
              {delegate?.title && (
                <p className="text-sm opacity-75">{delegate.title}</p>
              )}
            </div>
            <div className="absolute bottom-3 left-4 right-4 flex justify-between items-center">
              <Badge className="bg-white/20 text-white text-xs">
                {delegate?.tier_name || 'Attendee'}
              </Badge>
              <p className="text-xs opacity-50">Tap to flip</p>
            </div>
          </div>
        </div>
        
        {/* Back of Badge */}
        <div className="absolute w-full h-full backface-hidden rotate-y-180">
          <div className="w-full h-full bg-white rounded-xl shadow-lg p-4 border-2 border-indigo-100">
            <div className="text-center mb-3">
              <p className="text-xs text-slate-500">DIGITAL ID</p>
              <p className="font-mono text-sm">{delegate?.id?.slice(0, 12)}...</p>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="bg-slate-50 rounded p-2">
                <p className="text-slate-500">Status</p>
                <p className="font-medium text-green-600">
                  {delegate?.checked_in ? 'Checked In' : 'Registered'}
                </p>
              </div>
              <div className="bg-slate-50 rounded p-2">
                <p className="text-slate-500">Access</p>
                <p className="font-medium">
                  {delegate?.access?.vip_access ? 'VIP' : 'Standard'}
                </p>
              </div>
            </div>
            <div className="absolute bottom-3 left-4 right-4 flex gap-2">
              <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={(e) => { e.stopPropagation(); onDownload?.('badge'); }}>
                <FileDown className="h-3 w-3 mr-1" /> Download
              </Button>
              <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={(e) => { e.stopPropagation(); onDownload?.('share'); }}>
                <Share2 className="h-3 w-3 mr-1" /> Share
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Certificate Preview Component
const CertificatePreview = ({ certificate, event, onDownload, onOrder }) => {
  const certType = CERT_TYPES.find(t => t.value === certificate?.certificate_type) || CERT_TYPES[0];
  
  return (
    <Card className="overflow-hidden">
      <div className="relative">
        {/* Certificate Preview */}
        <div className="aspect-[1.4/1] bg-gradient-to-br from-slate-50 to-slate-100 p-6 border-4 border-double border-indigo-200">
          <div className="h-full flex flex-col items-center justify-center text-center">
            <Award className={`h-12 w-12 ${certType.color.replace('bg-', 'text-')} mb-2`} />
            <h3 className="text-lg font-bold text-slate-800">Certificate of {certType.label}</h3>
            <p className="text-sm text-slate-500 mt-1">This certifies that</p>
            <p className="text-xl font-bold text-indigo-600 mt-2">{certificate?.recipient_name}</p>
            <p className="text-sm text-slate-500 mt-1">has participated in</p>
            <p className="font-semibold text-slate-700">{event?.name}</p>
            <div className="mt-4 flex items-center gap-2">
              <Badge variant="outline" className="text-xs">
                <BadgeCheck className="h-3 w-3 mr-1" />
                Verified: {certificate?.verification_code}
              </Badge>
            </div>
          </div>
        </div>
        
        {/* Actions */}
        <div className="p-4 bg-white border-t flex justify-between items-center">
          <div>
            <p className="text-sm font-medium">{certificate?.title || 'Certificate'}</p>
            <p className="text-xs text-slate-500">
              Issued: {certificate?.issued_at ? new Date(certificate.issued_at).toLocaleDateString() : 'N/A'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => onDownload?.(certificate.id)}>
              <FileDown className="h-4 w-4 mr-1" /> PDF
            </Button>
            <Button size="sm" onClick={() => onOrder?.(certificate)}>
              <ShoppingCart className="h-4 w-4 mr-1" /> Order Print
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};

// Order Physical Certificate Dialog
const OrderCertificateDialog = ({ open, onOpenChange, certificate, eventId }) => {
  const [orderForm, setOrderForm] = useState({
    quantity: 1,
    frame: false,
    shipping_name: '',
    shipping_address: '',
    shipping_city: '',
    shipping_country: '',
    shipping_phone: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await api.post(`/admin/events/${eventId}/certificates/${certificate?.id}/order`, orderForm);
      toast.success('Order placed successfully! We will contact you for payment details.');
      onOpenChange(false);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to place order'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const basePrice = 25;
  const framePrice = orderForm.frame ? 15 : 0;
  const totalPrice = (basePrice + framePrice) * orderForm.quantity;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer className="h-5 w-5" />
            Order Physical Certificate
          </DialogTitle>
          <DialogDescription>
            Get a professionally printed certificate delivered to your address
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Product Options */}
          <div className="p-4 bg-slate-50 rounded-lg space-y-3">
            <div className="flex justify-between items-center">
              <div>
                <p className="font-medium">Premium Certificate Print</p>
                <p className="text-sm text-slate-500">High-quality paper, embossed seal</p>
              </div>
              <p className="font-bold">${basePrice}</p>
            </div>
            
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Switch 
                  checked={orderForm.frame} 
                  onCheckedChange={(checked) => setOrderForm({...orderForm, frame: checked})} 
                />
                <div>
                  <p className="font-medium">Add Frame</p>
                  <p className="text-sm text-slate-500">Elegant wooden frame</p>
                </div>
              </div>
              <p className="font-bold">+${framePrice > 0 ? framePrice : 15}</p>
            </div>

            <div className="flex items-center gap-4">
              <Label>Quantity</Label>
              <Select value={orderForm.quantity.toString()} onValueChange={(v) => setOrderForm({...orderForm, quantity: parseInt(v)})}>
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 5].map(n => <SelectItem key={n} value={n.toString()}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Shipping Info */}
          <div className="space-y-3">
            <h4 className="font-medium">Shipping Information</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Full Name *</Label>
                <Input 
                  value={orderForm.shipping_name} 
                  onChange={(e) => setOrderForm({...orderForm, shipping_name: e.target.value})}
                  placeholder="John Doe"
                />
              </div>
              <div>
                <Label>Phone *</Label>
                <Input 
                  value={orderForm.shipping_phone} 
                  onChange={(e) => setOrderForm({...orderForm, shipping_phone: e.target.value})}
                  placeholder="+1234567890"
                />
              </div>
            </div>
            <div>
              <Label>Address *</Label>
              <Input 
                value={orderForm.shipping_address} 
                onChange={(e) => setOrderForm({...orderForm, shipping_address: e.target.value})}
                placeholder="123 Main Street, Apt 4"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>City *</Label>
                <Input 
                  value={orderForm.shipping_city} 
                  onChange={(e) => setOrderForm({...orderForm, shipping_city: e.target.value})}
                  placeholder="New York"
                />
              </div>
              <div>
                <Label>Country *</Label>
                <Input 
                  value={orderForm.shipping_country} 
                  onChange={(e) => setOrderForm({...orderForm, shipping_country: e.target.value})}
                  placeholder="United States"
                />
              </div>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea 
                value={orderForm.notes} 
                onChange={(e) => setOrderForm({...orderForm, notes: e.target.value})}
                placeholder="Special instructions..."
                rows={2}
              />
            </div>
          </div>

          {/* Total */}
          <div className="p-4 bg-indigo-50 rounded-lg flex justify-between items-center">
            <div>
              <p className="text-sm text-slate-600">Total (excluding shipping)</p>
              <p className="text-xs text-slate-500">Shipping calculated at checkout</p>
            </div>
            <p className="text-2xl font-bold text-indigo-600">${totalPrice}</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            disabled={isSubmitting || !orderForm.shipping_name || !orderForm.shipping_address}
          >
            {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShoppingCart className="h-4 w-4 mr-2" />}
            Place Order
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Component
const DigitalCredentials = ({ eventId, delegateId = null }) => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('badges');
  const [showCreateCert, setShowCreateCert] = useState(false);
  const [showOrder, setShowOrder] = useState(false);
  const [selectedCert, setSelectedCert] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showBadgeDesigner, setShowBadgeDesigner] = useState(false);
  const [showCertDesigner, setShowCertDesigner] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Create certificate form
  const [certForm, setCertForm] = useState({
    delegate_id: delegateId || '',
    certificate_type: 'attendance',
    title: 'Certificate of Attendance',
    recipient_name: '',
    description: ''
  });

  // Fetch delegates
  const { data: delegates = [] } = useQuery({
    queryKey: ['delegates', eventId],
    queryFn: () => api.get(`/events/${eventId}/delegates`).then(r => r.data || []),
    enabled: !!eventId,
  });

  // Fetch certificates
  const { data: certificates = [] } = useQuery({
    queryKey: ['certificates', eventId],
    queryFn: () => api.get(`/events/${eventId}/certificates`).then(r => r.data || []),
    enabled: !!eventId,
  });

  // Fetch event
  const { data: event } = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => api.get(`/events/${eventId}`).then(r => r.data),
    enabled: !!eventId,
  });

  // Create certificate mutation
  const createCertMutation = useMutation({
    mutationFn: (data) => api.post(`/admin/events/${eventId}/certificates`, data),
    onSuccess: () => {
      toast.success('Certificate created successfully');
      queryClient.invalidateQueries(['certificates', eventId]);
      setShowCreateCert(false);
      setCertForm({ delegate_id: '', certificate_type: 'attendance', title: 'Certificate of Attendance', recipient_name: '', description: '' });
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to create certificate')),
  });

  // Bulk generate mutation
  const bulkGenerateMutation = useMutation({
    mutationFn: (data) => api.post(`/admin/events/${eventId}/certificates/bulk-generate`, null, { params: data }),
    onSuccess: (response) => {
      toast.success(`Generated ${response.data?.count || 0} certificates`);
      queryClient.invalidateQueries(['certificates', eventId]);
    },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to generate certificates')),
  });

  // Helper function to get auth token consistently — delegates to the central
  // /lib/authToken helper so we have ONE source of truth.
  const getToken = sharedGetToken;

  // Download functions
  const downloadBadgePDF = async (delegateId) => {
    try {
      const token = getToken();
      if (!token) {
        toast.error('Session expired. Please log in again.');
        return;
      }
      
      // Use the correct badge-pdf endpoint
      const response = await fetch(`${API_URL}/api/events/${eventId}/delegates/${delegateId}/badge-pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `badge_${delegateId.slice(0,8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Badge downloaded!');
      } else if (response.status === 401) {
        toast.error('Session expired. Please log in again.');
      } else if (response.status === 404) {
        toast.error('Badge not found. The delegate may not have a badge configured.');
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || 'Failed to download badge');
      }
    } catch (err) {
      console.error('Badge download error:', err);
      toast.error('Failed to download badge. Please check your connection.');
    }
  };

  const downloadCertificatePDF = async (certId) => {
    try {
      const token = getToken();
      if (!token) {
        toast.error('Session expired. Please log in again.');
        return;
      }
      
      const response = await fetch(`${API_URL}/api/events/${eventId}/certificates/${certId}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `certificate_${certId.slice(0,8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Certificate downloaded!');
      } else if (response.status === 401) {
        toast.error('Session expired. Please log in again.');
      } else if (response.status === 404) {
        toast.error('Certificate not found.');
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || 'Failed to download certificate');
      }
    } catch (err) {
      console.error('Certificate download error:', err);
      toast.error('Failed to download certificate. Please check your connection.');
    }
  };

  // Filter delegates
  const filteredDelegates = delegates.filter(d => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return d.name?.toLowerCase().includes(query) || d.email?.toLowerCase().includes(query);
  });

  return (
    <div className="space-y-6" data-testid="digital-credentials">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Digital Credentials</h2>
          <p className="text-slate-500">Manage digital ID badges and certificates</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => bulkGenerateMutation.mutate({ certificate_type: 'attendance', title: 'Certificate of Attendance' })}>
            <Users className="h-4 w-4 mr-2" />
            Generate All Certificates
          </Button>
          <Button onClick={() => setShowCreateCert(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Issue Certificate
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="badges">Digital ID Badges</TabsTrigger>
          <TabsTrigger value="certificates">Certificates</TabsTrigger>
          <TabsTrigger value="orders">Print Orders</TabsTrigger>
        </TabsList>

        {/* Digital Badges Tab */}
        <TabsContent value="badges" className="space-y-4">
          <div className="flex gap-4 items-center flex-wrap">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input 
                placeholder="Search delegates..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button variant="outline" onClick={() => setShowBadgeDesigner(true)}>
              <Palette className="h-4 w-4 mr-2" />
              Design Badge Template
            </Button>
            <Button variant="outline" onClick={async () => {
              // Download all badges as PDF
              toast.info('Generating bulk badges PDF... This may take a moment.');
              try {
                const token = getToken();
                if (!token) {
                  toast.error('Session expired. Please log in again.');
                  return;
                }
                
                const response = await fetch(`${API_URL}/api/events/${eventId}/badges/bulk-pdf`, {
                  method: 'POST',
                  headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({ page_size: 'letter', badges_per_page: 6 })
                });
                
                if (response.ok) {
                  const blob = await response.blob();
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `badges_all_${new Date().toISOString().split('T')[0]}.pdf`;
                  document.body.appendChild(a);
                  a.click();
                  window.URL.revokeObjectURL(url);
                  document.body.removeChild(a);
                  toast.success('Bulk badges PDF downloaded!');
                } else if (response.status === 401) {
                  toast.error('Session expired. Please log in again.');
                } else if (response.status === 404) {
                  toast.error('No delegates found for this event.');
                } else {
                  const errorData = await response.json().catch(() => ({}));
                  toast.error(errorData.detail || 'Failed to generate bulk badges');
                }
              } catch (err) {
                console.error('Bulk badges error:', err);
                toast.error('Failed to generate bulk badges. Please check your connection.');
              }
            }}>
              <Printer className="h-4 w-4 mr-2" />
              Print All Badges
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredDelegates.slice(0, 20).map((delegate) => (
              <Card key={delegate.id} className="overflow-hidden">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold">
                      {delegate.name?.[0] || 'A'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold truncate">{delegate.name}</p>
                      <p className="text-sm text-slate-500 truncate">{delegate.email}</p>
                      {delegate.company && (
                        <p className="text-sm text-slate-400 truncate">{delegate.company}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Badge variant={delegate.checked_in ? 'default' : 'secondary'} className="text-xs">
                      {delegate.checked_in ? 'Checked In' : delegate.status || 'Registered'}
                    </Badge>
                    {delegate.access?.meal_access && (
                      <Badge variant="outline" className="text-xs">Meals</Badge>
                    )}
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" variant="outline" className="flex-1" onClick={() => downloadBadgePDF(delegate.id)}>
                      <FileDown className="h-3 w-3 mr-1" /> Badge
                    </Button>
                    <Button size="sm" variant="outline" className="flex-1" onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/verify/${delegate.id}`);
                      toast.success('Badge link copied!');
                    }}>
                      <Share2 className="h-3 w-3 mr-1" /> Share
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Certificates Tab */}
        <TabsContent value="certificates" className="space-y-4">
          <div className="flex gap-4 items-center justify-end mb-4">
            <Button variant="outline" onClick={() => setShowCertDesigner(true)}>
              <Palette className="h-4 w-4 mr-2" />
              Design Certificate Template
            </Button>
          </div>
          {certificates.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Award className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Certificates Yet</h3>
                <p className="text-slate-500 mb-4">Generate certificates for your event attendees</p>
                <Button onClick={() => setShowCreateCert(true)}>
                  <Plus className="h-4 w-4 mr-2" /> Issue First Certificate
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {certificates.map((cert) => (
                <CertificatePreview 
                  key={cert.id} 
                  certificate={cert} 
                  event={event}
                  onDownload={downloadCertificatePDF}
                  onOrder={(c) => { setSelectedCert(c); setShowOrder(true); }}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Orders Tab */}
        <TabsContent value="orders" className="space-y-4">
          <Card>
            <CardContent className="py-12 text-center">
              <ShoppingCart className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Print Orders</h3>
              <p className="text-slate-500">Orders for physical certificates will appear here</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Certificate Dialog */}
      <Dialog open={showCreateCert} onOpenChange={setShowCreateCert}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Issue Certificate</DialogTitle>
            <DialogDescription>Create a certificate for an attendee</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Select Attendee *</Label>
              <Select value={certForm.delegate_id} onValueChange={(v) => {
                const delegate = delegates.find(d => d.id === v);
                setCertForm({...certForm, delegate_id: v, recipient_name: delegate?.name || ''});
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="Select attendee..." />
                </SelectTrigger>
                <SelectContent>
                  {delegates.map(d => (
                    <SelectItem key={d.id} value={d.id}>{d.name} ({d.email})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type</Label>
                <Select value={certForm.certificate_type} onValueChange={(v) => setCertForm({...certForm, certificate_type: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CERT_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Title *</Label>
                <Input 
                  value={certForm.title} 
                  onChange={(e) => setCertForm({...certForm, title: e.target.value})}
                  placeholder="Certificate of Attendance"
                />
              </div>
            </div>
            <div>
              <Label>Recipient Name *</Label>
              <Input 
                value={certForm.recipient_name} 
                onChange={(e) => setCertForm({...certForm, recipient_name: e.target.value})}
                placeholder="As it will appear on certificate"
              />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Textarea 
                value={certForm.description} 
                onChange={(e) => setCertForm({...certForm, description: e.target.value})}
                placeholder="Additional text to appear on certificate"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateCert(false)}>Cancel</Button>
            <Button 
              onClick={() => createCertMutation.mutate(certForm)}
              disabled={createCertMutation.isPending || !certForm.delegate_id || !certForm.recipient_name}
            >
              {createCertMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Award className="h-4 w-4 mr-2" />}
              Issue Certificate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order Physical Certificate Dialog */}
      <OrderCertificateDialog 
        open={showOrder} 
        onOpenChange={setShowOrder}
        certificate={selectedCert}
        eventId={eventId}
      />

      {/* Badge Designer Dialog */}
      <Dialog open={showBadgeDesigner} onOpenChange={setShowBadgeDesigner}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Badge Designer</DialogTitle>
            <DialogDescription>Customize your event badge template</DialogDescription>
          </DialogHeader>
          <BadgeDesigner eventId={eventId} />
        </DialogContent>
      </Dialog>

      {/* Certificate Designer Dialog */}
      <Dialog open={showCertDesigner} onOpenChange={setShowCertDesigner}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Certificate Designer</DialogTitle>
            <DialogDescription>Customize your certificate template</DialogDescription>
          </DialogHeader>
          <CertificateDesigner eventId={eventId} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DigitalCredentials;
